/* eslint-disable */
/* prettier-ignore */
// Generated by elegant-router
// Read more: https://github.com/soybeanjs/elegant-router

import type { RouteRecordRaw, RouteComponent } from 'vue-router';
import type { ElegantConstRoute } from '@elegant-router/vue';
import type { RouteMap, RouteKey, RoutePath } from '@elegant-router/types';

/**
 * transform elegant const routes to vue routes
 * @param routes elegant const routes
 * @param layouts layout components
 * @param views view components
 */
export function transformElegantRoutesToVueRoutes(
  routes: ElegantConstRoute[],
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  return routes.flatMap(route => transformElegantRouteToVueRoute(route, layouts, views));
}

/**
 * transform elegant route to vue route
 * @param route elegant const route
 * @param layouts layout components
 * @param views view components
 */
function transformElegantRouteToVueRoute(
  route: ElegantConstRoute,
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  const LAYOUT_PREFIX = 'layout.';
  const VIEW_PREFIX = 'view.';
  const ROUTE_DEGREE_SPLITTER = '_';
  const FIRST_LEVEL_ROUTE_COMPONENT_SPLIT = '$';

  function isLayout(component: string) {
    return component.startsWith(LAYOUT_PREFIX);
  }

  function getLayoutName(component: string) {
    return component.replace(LAYOUT_PREFIX, '');
  }

  function isView(component: string) {
    return component.startsWith(VIEW_PREFIX);
  }

  function getViewName(component: string) {
    return component.replace(VIEW_PREFIX, '');
  }

  function isFirstLevelRoute(item: ElegantConstRoute) {
    return !item.name.includes(ROUTE_DEGREE_SPLITTER);
  }

  function isSingleLevelRoute(item: ElegantConstRoute) {
    return isFirstLevelRoute(item) && !item.children?.length;
  }

  function getSingleLevelRouteComponent(component: string) {
    const [layout, view] = component.split(FIRST_LEVEL_ROUTE_COMPONENT_SPLIT);

    return {
      layout: getLayoutName(layout),
      view: getViewName(view)
    };
  }

  const vueRoutes: RouteRecordRaw[] = [];

  // add props: true to route
  if (route.path.includes(':') && !route.props) {
    route.props = true;
  }

  const { name, path, component, children, ...rest } = route;

  const vueRoute = { name, path, ...rest } as RouteRecordRaw;

  if (component) {
    if (isSingleLevelRoute(route)) {
      const { layout, view } = getSingleLevelRouteComponent(component);

      const singleLevelRoute: RouteRecordRaw = {
        path,
        component: layouts[layout],
        children: [
          {
            name,
            path: '',
            component: views[view],
            ...rest
          } as RouteRecordRaw
        ]
      };

      return [singleLevelRoute];
    }

    if (isLayout(component)) {
      const layoutName = getLayoutName(component);

      vueRoute.component = layouts[layoutName];
    }

    if (isView(component)) {
      const viewName = getViewName(component);

      vueRoute.component = views[viewName];
    }

  }
  
  // add redirect to child
  if (children?.length && !vueRoute.redirect) {
    vueRoute.redirect = {
      name: children[0].name
    };
  }
  
  if (children?.length) {
    const childRoutes = children.flatMap(child => transformElegantRouteToVueRoute(child, layouts, views));

    if(isFirstLevelRoute(route)) {
      vueRoute.children = childRoutes;
    } else {
      vueRoutes.push(...childRoutes);
    }
  }

  vueRoutes.unshift(vueRoute);

  return vueRoutes;
}

/**
 * map of route name and route path
 */
const routeMap: RouteMap = {
  "root": "/",
  "not-found": "/:pathMatch(.*)*",
  "exception": "/exception",
  "exception_403": "/exception/403",
  "exception_404": "/exception/404",
  "exception_500": "/exception/500",
  "403": "/403",
  "404": "/404",
  "500": "/500",
  "application": "/application",
  "application_account-reset": "/application/account-reset",
  "application_application-version": "/application/application-version",
  "application_develop": "/application/develop",
  "application_develop-detail": "/application/develop-detail",
  "application_protocal-manage": "/application/protocal-manage",
  "client": "/client",
  "client_management": "/client/management",
  "client_management-add": "/client/management-add",
  "client_management-edit": "/client/management-edit",
  "client_platform": "/client/platform",
  "data": "/data",
  "data_all-screen": "/data/all-screen",
  "data_app-chart": "/data/app-chart",
  "data_product-chart": "/data/product-chart",
  "home": "/home",
  "login": "/login/:module(pwd-login|reset-pwd)?",
  "product": "/product",
  "product_debug": "/product/debug",
  "product_debug-add": "/product/debug-add",
  "product_debug-detail": "/product/debug-detail",
  "product_develop": "/product/develop",
  "product_develop-add": "/product/develop-add",
  "product_develop-detail": "/product/develop-detail",
  "product_develop-lang": "/product/develop-lang",
  "product_device": "/product/device",
  "product_device-detail": "/product/device-detail",
  "product_diagnosis": "/product/diagnosis",
  "product_firmware": "/product/firmware",
  "product_firmware-add": "/product/firmware-add",
  "product_firmware-list": "/product/firmware-list",
  "product_firmware-task-detail": "/product/firmware-task-detail",
  "product_log": "/product/log",
  "system": "/system",
  "system_account": "/system/account",
  "system_account-add": "/system/account-add",
  "system_account-permission": "/system/account-permission",
  "system_api-key": "/system/api-key",
  "system_api-key-audit": "/system/api-key-audit",
  "system_application-detail": "/system/application-detail",
  "system_application-template": "/system/application-template",
  "system_category": "/system/category",
  "system_category-add": "/system/category-add",
  "system_category-detail": "/system/category-detail",
  "system_firmwaretype": "/system/firmwaretype",
  "system_firmwaretype-lang": "/system/firmwaretype-lang",
  "system_menu": "/system/menu",
  "system_menu-lang": "/system/menu-lang",
  "system_role": "/system/role",
  "user": "/user",
  "user_center": "/user/center",
  "user_company": "/user/company",
  "user_reset-email": "/user/reset-email",
  "user_reset-password": "/user/reset-password"
};

/**
 * get route path by route name
 * @param name route name
 */
export function getRoutePath<T extends RouteKey>(name: T) {
  return routeMap[name];
}

/**
 * get route name by route path
 * @param path route path
 */
export function getRouteName(path: RoutePath) {
  const routeEntries = Object.entries(routeMap) as [RouteKey, RoutePath][];

  const routeName: RouteKey | null = routeEntries.find(([, routePath]) => routePath === path)?.[0] || null;

  return routeName;
}
